import { ReactNode, useRef } from 'react';
import ComboBoxField from '@/components/select/ComboBoxField';
import { ChecklistComboBoxProps, ComboBoxItem } from '@/components/select/comboBoxTypes';
import useComboBoxQuery from '@/components/select/useComboBoxQuery';
import { Combobox } from '@headlessui/react';

const ChecklistComboBox = <T extends ComboBoxItem>({
  name,
  label,
  hideLabel,
  placeholder,
  queryKey = name ? [name] : [],
  queryFn,
  selectedIds = [],
  onSelect,
  onDeselect,
  disabled = false,
  delay = 500,
  closeOnBlur = true,
  openOnFocus = true,
  showEmptyState = false,
}: ChecklistComboBoxProps<T>): ReactNode => {
  const isInitializedRef = useRef(true);
  const comboBox = useComboBoxQuery<T>({
    queryKey,
    queryFn,
    isInitializedRef,
    delay,
    closeOnBlur,
    openOnFocus,
    keepPreviousResults: true,
  });
  const selectedIdSet = new Set(selectedIds.map(String));
  const selectedItems = comboBox.items.filter((item) => selectedIdSet.has(String(item.id)));

  const handleChange = (selected: T[]) => {
    const nextSelectedIdSet = new Set(selected.map((item) => String(item.id)));
    const deselectedItem = selectedItems.find((item) => !nextSelectedIdSet.has(String(item.id)));
    if (deselectedItem) {
      if (onDeselect) onDeselect(deselectedItem);
      return;
    }

    const selectedItem = selected.find((item) => !selectedIdSet.has(String(item.id)));
    if (selectedItem) onSelect(selectedItem);
  };

  const handleClearQuery = () => {
    comboBox.clearQuery();

    setTimeout(() => {
      comboBox.inputRef.current?.blur();
      comboBox.inputRef.current?.focus();
    });
  };

  return (
    <Combobox
      value={selectedItems}
      onChange={handleChange}
      disabled={disabled}
      immediate={openOnFocus}
      multiple
    >
      {({ open }) => (
        <ComboBoxField<T>
          label={label}
          hideLabel={hideLabel}
          placeholder={placeholder}
          inputRef={comboBox.inputRef}
          open={open}
          items={comboBox.items}
          isLoading={comboBox.isLoading}
          showEmptyOption={false}
          displayValue={() => comboBox.inputValue}
          onFocus={comboBox.handleFocus}
          onBlur={comboBox.handleBlur}
          onAfterLeave={() => {}}
          onDeselect={() => {}}
          onClearQuery={comboBox.inputValue ? handleClearQuery : undefined}
          showEmptyState={showEmptyState}
          modal={false}
        />
      )}
    </Combobox>
  );
};

export default ChecklistComboBox;
